import classnames from 'classnames'
import { LoanOffer, SpecialOffer } from 'embed/constants/models'
import { css } from 'emotion'
import { LoanPurposeOptions, Product, RedirectType } from 'even-ts-static'
import * as React from 'react'

import { LoanOfferListContainer } from 'embed/components/LoanOfferList/container'
import SeeAllOffersButton from 'embed/components/SeeAllOffersButton'
import SpecialOfferList from 'embed/components/SpecialOfferList/container'
import { SpecialOfferListItem } from 'embed/components/SpecialOfferList/list-item'
import { SpecialOfferListItemCompact } from 'embed/components/SpecialOfferList/list-item-compact'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { Timer } from 'embed/components/Timer'
import {
  Experiment,
  LoanOffersFilterDrawer,
  Original,
  ShowAllOffersOnResultsStep,
  Variant,
  withExperiment,
} from 'embed/experiments'
import OnceLoaded from '../../OnceLoaded'
import ProgressRing from '../../ProgressRing'
import { applyFilters, FilterDrawer } from '../FilterDrawer'
import LoanOfferListItem from '../ListItem'
import SelectedFilterPills from '../LoanFilter/SelectedFilterPills'
import { CreateAccount } from './CreateAccount'
import EmptyState from './EmptyState'
import { CommonState } from 'embed/reducers'
import { configAllowPersonalizedLinkoutSelector, isFionaAccountCreationEnabled } from 'embed/selectors/config'
import { connect } from 'react-redux'
import { loanApplicationFirstNameSelector } from 'embed/selectors/application'
import { capitalize } from 'ui/multi-product/utils'

const FilterDrawerExperiment = withExperiment(LoanOffersFilterDrawer)(FilterDrawer)
const CheckGreen = require('embed/components/ApplicationField/check-green.svg')

export enum DisplayOfferType {
  Loan = 'loan',
  Special = 'special',
}

const styles = require('./styles.scss')

interface OwnProps {
  appPurpose?: LoanPurposeOptions
  loading?: boolean
  bestOffer: LoanOffer | SpecialOffer
  displayOfferType: DisplayOfferType
  dispatchGetLoanOffer: (offer: LoanOffer, redirectType: RedirectType) => void
  email: string
  filteredLenderId?: string
  isCompactResults?: boolean
  isLoggedIn: boolean
  isResultsView: boolean
  loanOffers: LoanOffer[]
  loanOffersByLender: { [s: string]: LoanOffer[] }
  onTimerComplete: () => void
  preferredOffer?: LoanOffer
  preferredOfferPartner?: string
  preferredOfferPartnerName?: string
  redirectTimerStarted: boolean
  selectedVariantId?: string
  shouldRedirect: boolean
  specialOffers: SpecialOffer[]
}

interface State {
  allowPersonalizedLinkout: boolean
  enableAccountCreation: boolean
  firstName?: string
}

type Props = OwnProps & State

// eslint-disable-next-line complexity
const Render: React.FC<Props> = ({
  allowPersonalizedLinkout,
  appPurpose,
  bestOffer,
  enableAccountCreation,
  dispatchGetLoanOffer,
  displayOfferType,
  email,
  filteredLenderId,
  firstName,
  isCompactResults,
  isLoggedIn,
  isResultsView,
  loading,
  loanOffers,
  loanOffersByLender,
  onTimerComplete,
  preferredOffer,
  preferredOfferPartnerName,
  redirectTimerStarted,
  selectedVariantId,
  shouldRedirect,
  specialOffers,
}) => {
  const [limit, setLimit] = React.useState(8)
  const [isAllOffersButtonShown, toggleSeeAllOffersButton] = React.useState(isResultsView && loanOffers.length > 8)
  const handleSeeMoreOffers = () => {
    setLimit(loanOffers.length)
    toggleSeeAllOffersButton(false)
  }

  const isStudentLoanRefi = appPurpose === LoanPurposeOptions.STUDENT_REFINANCE
  const resultsBodyText = isStudentLoanRefi
    ? 'Here are a few tips to keep in mind when refinancing your student loans.'
    : 'Here are a few tips to keep in mind when applying for personal loans:'

  const noOffersBodyText = `Unfortunately, it looks like there are no offers available for you at this time.${
    isStudentLoanRefi ? '‡' : ''
  }`

  const hasOffers = loanOffers.length > 0 || specialOffers.length > 0

  const [filterState, setFilterState] = React.useState({ selectedLenders: [] })
  const filteredOffers = applyFilters(filterState, loanOffers)

  const showSeeMoreOffers = selectedVariantId !== ShowAllOffersOnResultsStep.variants.ShowAllOffers.id

  const getCongratulationsText = () => {
    if (allowPersonalizedLinkout) {
      return firstName ? `Congratulations, ${capitalize(firstName)}!` : `Congratulations!`
    } else {
      return 'Congratulations!'
    }
  }
  const getRedirectText = () =>
    `${getCongratulationsText()} We will redirect you to an offer from one of our partners momentarily!`
  const redirectMessage = (
    <Timer onComplete={onTimerComplete} seconds={10} started={redirectTimerStarted}>
      {(props) => (
        <ThemeConsumer>
          {({ borderRadiusLarge, backdropStyles, boxStyles, colorBorder, colorPrimary }) => (
            <div
              className={classnames(
                styles.headerContainer,
                { [styles.headerContainerCompact]: isCompactResults },
                styles.timerContainer,
                css`
                  background-color: ${backdropStyles.backgroundColor};
                  border-color: ${colorBorder};
                  border-radius: ${borderRadiusLarge};
                `,
              )}
              style={boxStyles}
            >
              <div
                className={classnames(
                  styles.timerCount,
                  css`
                    background: ${backdropStyles.backgroundColor};
                  `,
                )}
              >
                <ProgressRing
                  className={styles.timerProgress}
                  percentRemaining={1 - (props?.secondsRemaining || 10) / 10}
                  progressColor={colorPrimary}
                  ringColor={colorBorder}
                />
                {props?.secondsRemaining}
              </div>
              <h2>{getRedirectText()}</h2>
            </div>
          )}
        </ThemeConsumer>
      )}
    </Timer>
  )
  const getNoRedirectText = () => `${getCongratulationsText()} We've matched you with the following offers`
  const noRedirectMessage = (
    <ThemeConsumer>
      {({ borderRadiusLarge, backdropStyles, boxStyles, colorBorder }) => (
        <div
          className={classnames(
            styles.headerContainer,
            { [styles.headerContainerCompact]: isCompactResults },
            css`
              background-color: ${backdropStyles.backgroundColor};
              border-color: ${colorBorder};
              border-radius: ${borderRadiusLarge};
            `,
          )}
          style={boxStyles}
        >
          <h2>{getNoRedirectText()}</h2>
        </div>
      )}
    </ThemeConsumer>
  )

  const noOffersMessage = (
    <ThemeConsumer>
      {({ borderRadiusLarge, backdropStyles, boxStyles, colorBorder, colorTextSecondary }) => (
        <React.Fragment>
          <div
            className={classnames(
              styles.headerContainer,
              { [styles.headerContainerCompact]: isCompactResults },
              css`
                background-color: ${backdropStyles.backgroundColor};
                border-color: ${colorBorder};
                border-radius: ${borderRadiusLarge};
              `,
            )}
            style={boxStyles}
          >
            <h2>{noOffersBodyText}</h2>
          </div>
          <p
            className={classnames(
              styles.resultsBody,
              css`
                color: ${colorTextSecondary};
              `,
            )}
          >
            {!isCompactResults
              ? resultsBodyText
              : 'Please try again at a later date to see if new offers have become available that fit your needs.'}
          </p>
          {!isCompactResults ? <EmptyState /> : null}
        </React.Fragment>
      )}
    </ThemeConsumer>
  )

  const preferredOfferCongratsMessage = shouldRedirect ? (
    redirectMessage
  ) : (
    <ThemeConsumer>
      {({ borderRadiusLarge, backdropStyles, boxStyles, colorBorder }) => (
        <div
          className={classnames(
            styles.headerContainer,
            { [styles.headerContainerCompact]: isCompactResults },
            css`
              background-color: ${backdropStyles.backgroundColor};
              border-color: ${colorBorder};
              border-radius: ${borderRadiusLarge};
            `,
          )}
          style={boxStyles}
        >
          <h2>{getNoRedirectText()}</h2>
        </div>
      )}
    </ThemeConsumer>
  )

  let message
  if (preferredOffer) {
    // Preferred Offer Partner had an offer
    const manualLoanOfferDispatch = (offer) => dispatchGetLoanOffer(offer, RedirectType.MANUAL)
    message = (
      <div>
        <div>{preferredOfferCongratsMessage}</div>
        <LoanOfferListItem
          dispatchOfferCta={manualLoanOfferDispatch}
          isResultsView={true}
          offer={preferredOffer}
          shouldDisplayTransunionDisclaimer={false}
        />
        {hasOffers ? <h2>Other offers recommended for you</h2> : null}
      </div>
    )
  } else if (hasOffers && preferredOfferPartnerName) {
    // Preferred Offer Partner set up but no offer found
    message = (
      <div>
        <div>{preferredOfferCongratsMessage}</div>
        <div style={{ padding: '0px 24px 24px', textAlign: 'center' }}>
          <em>Unfortunately, {preferredOfferPartnerName} was not able to provide you with an offer at this time</em>
        </div>
      </div>
    )
  } else if (hasOffers) {
    // If we have offers to display, show the appropriate "congratulations" message
    if (shouldRedirect) {
      message = redirectMessage
    } else {
      message = (
        <Experiment experiment={LoanOffersFilterDrawer}>
          <Original>{noRedirectMessage}</Original>
          <Variant
            experiment={LoanOffersFilterDrawer}
            variant={LoanOffersFilterDrawer.variants.AllOffersWithFilterDrawer}
          >
            <FilterDrawerExperiment
              loanOffers={loanOffers}
              drawerText={getNoRedirectText()}
              setFilterState={setFilterState}
            />
          </Variant>
        </Experiment>
      )
    }
  } else {
    message = noOffersMessage
  }

  const createAccountSuccessMessage = (
    <ThemeConsumer>
      {({ borderRadiusSmall, colorBorder }) => (
        <div
          className={classnames(
            styles.createAccountSuccessMessage,
            css`
              border-color: ${colorBorder};
              border-radius: ${borderRadiusSmall};
            `,
          )}
        >
          <h4 className={styles.createAccountSuccessMessageHeader}>
            <CheckGreen /> Account created!
          </h4>
          <p className={styles.createAccountSuccessMessageDescription}>
            Your account is ready, and we have sent a confirmation email to {email}
          </p>
        </div>
      )}
    </ThemeConsumer>
  )

  return (
    <React.Fragment>
      <OnceLoaded product={Product.Loan}>
        {message}
        {enableAccountCreation && isLoggedIn && createAccountSuccessMessage}
      </OnceLoaded>
      {displayOfferType === DisplayOfferType.Loan ? (
        <React.Fragment>
          {filteredLenderId !== undefined && (
            <ThemeConsumer>
              {({ colorTextSecondary }) => (
                <div className={styles.filterPillContainer}>
                  <div
                    className={classnames(
                      styles.filterPillContainerTitle,
                      css`
                        color: ${colorTextSecondary};
                      `,
                    )}
                  >
                    Show only:
                  </div>
                  <SelectedFilterPills />
                </div>
              )}
            </ThemeConsumer>
          )}
          <Experiment experiment={LoanOffersFilterDrawer}>
            <Original>
              <LoanOfferListContainer
                allLoanOffersByLender={loanOffersByLender}
                bestOffer={bestOffer as LoanOffer}
                ctaButtonText="Get Loan"
                ctaLabel="Complete the loan application with selected provider"
                dispatchOfferCta={dispatchGetLoanOffer}
                isResultsView={isResultsView}
                limit={limit}
                listItem={LoanOfferListItem}
                loanOffers={loanOffers}
                showSeeMoreOffers={showSeeMoreOffers}
              />
            </Original>
            <Variant
              experiment={LoanOffersFilterDrawer}
              variant={LoanOffersFilterDrawer.variants.AllOffersWithFilterDrawer}
            >
              <LoanOfferListContainer
                allLoanOffersByLender={loanOffersByLender}
                bestOffer={bestOffer as LoanOffer}
                ctaButtonText="Get Loan"
                ctaLabel="Complete the loan application with selected provider"
                dispatchOfferCta={dispatchGetLoanOffer}
                isResultsView={isResultsView}
                limit={limit}
                listItem={LoanOfferListItem}
                loanOffers={filteredOffers}
                showSeeMoreOffers={showSeeMoreOffers}
              />
            </Variant>
          </Experiment>
          {isAllOffersButtonShown && !loading && <SeeAllOffersButton handleSeeMoreOffers={handleSeeMoreOffers} />}
        </React.Fragment>
      ) : (
        <SpecialOfferList
          listItem={isCompactResults ? SpecialOfferListItemCompact : SpecialOfferListItem}
          bestOffer={specialOffers[0]}
          isResultsView={isResultsView}
          product={Product.Loan}
          specialOffers={specialOffers}
        />
      )}
      {enableAccountCreation && (
        <OnceLoaded product={Product.Loan}>{!isLoggedIn ? <CreateAccount /> : createAccountSuccessMessage}</OnceLoaded>
      )}
      <OnceLoaded product={Product.Loan}>
        <ThemeConsumer>
          {({ colorTextSecondary, fontFamilySecondary }) => (
            <p
              className={classnames(
                styles.disclosure,
                css`
                  color: ${colorTextSecondary};
                  font-family: ${fontFamilySecondary};
                `,
              )}
            >
              {isStudentLoanRefi && (
                <span>
                  ‡If you did not receive an offer from a specific Lending Partner, they may not have been able to
                  determine whether you qualified for student loan refinancing based on the information you provided. No
                  credit decision has been made. You may still qualify for student loan refinancing from our partners.
                </span>
              )}
              {!isStudentLoanRefi && (
                <span>
                  If you did not receive an offer from a specific Lending Partner, they may not have been able to
                  determine whether you qualified for a personal loan based on the information you provided. No credit
                  decision has been made. You may still qualify for a personal loan from our partners.
                </span>
              )}
            </p>
          )}
        </ThemeConsumer>
      </OnceLoaded>
    </React.Fragment>
  )
}

const mapStateToProps = (state: CommonState) => ({
  allowPersonalizedLinkout: configAllowPersonalizedLinkoutSelector(state),
  enableAccountCreation: isFionaAccountCreationEnabled(state),
  firstName: loanApplicationFirstNameSelector(state),
})

export const LoanResultsView = withExperiment(ShowAllOffersOnResultsStep)(connect(mapStateToProps)(Render))
